/**
 * The project's phases as columns, with the tasks filed under them as cards.
 *
 * The milestones tab has always been able to say WHEN a phase is due and how
 * much of it is ticked; it has never been able to show WHAT is in one. The list
 * next door answers "when", this answers "what", and the pills in the toolbar
 * choose between them. It is its own file because `Milestones.tsx` was already
 * at 387 lines and a board inside it would have pushed the tab past the ceiling
 * AGENTS.md §4 sets - the same split `Roadmap.tsx` and `RoadmapCard.tsx` made.
 * It is split the same way twice over: this file owns what the board contains,
 * what a move writes and what a refusal does, while the column head and the card
 * are in `MilestoneBoardCard.tsx` and cannot reach the API at all. The one piece
 * of arithmetic a screenshot cannot check - what a move does to the tallies -
 * is React-free in `milestoneBoardMove.ts`, with its own test.
 *
 * ## One request, not one per column
 *
 * `GET /projects/:id/milestones?withTasks=1` returns the columns already
 * grouped, tallied and ordered (`crm-projects/milestones.routes.ts`). The board
 * does not re-derive any of it:
 *
 *  - the ORDER is the server's, unfiled column last;
 *  - the COUNTS are the server's, and are not `tasks.length` - on a project past
 *    the endpoint's card cap the tallies count the whole phase while the cards
 *    are a prefix, which is what the line under the board says out loud;
 *  - the UNFILED COLUMN IS ALWAYS THERE. A task with no phase is an ordinary
 *    task (`Task.milestoneId` is nullable and its foreign key is SET NULL, so
 *    deleting a phase deliberately makes more of them), and a board that hid
 *    them would show fewer tasks than the project has with nothing on screen
 *    admitting it. It is also the only way to UNFILE a card from here.
 *
 * ## Moving a card is a menu, not a drag
 *
 * Checked before writing a line of it: `tasks/TaskBoard.tsx` is already a board
 * of columns and cards, and its move is a menu (`TaskRowMenu`, "Move to"); the
 * roadmap board's is a menu too (`MoveMenu` in `RoadmapCard.tsx`, reused here).
 * There is no drag anywhere in this section to copy, and the repo has been
 * bitten twice trying - `setPointerCapture` swallowing the click that was meant
 * to open a card, and a drag that could not be driven at all. So the phase move
 * is the same control the two neighbouring boards use: one menu on the card,
 * every column in it including the unfiled one, reachable from the keyboard
 * because `Pop` gives its rows roving focus. A drag can be added on top later
 * without taking this away; a drag INSTEAD of this would be a board an operator
 * on a phone or a keyboard cannot move anything on.
 *
 * ## A refused move has to be visible
 *
 * The card moves optimistically, because a card that waits for a round trip
 * reads as a click that did nothing. When the write fails the columns are put
 * back to exactly what they were and the board reloads - a card left sitting in
 * the column it never reached is the failure an operator does not double-check.
 * The sentence comes from `taskErrors.ts`, so "that phase belongs to another
 * project" and "that phase has been deleted" say so, instead of sending somebody
 * to look for a network problem that is not there.
 *
 * ## On a phone
 *
 * The columns scroll sideways, exactly like the roadmap board and the task
 * board - `ScrollFade` fades the edge that still has something behind it. At
 * 375px one 272px column is on screen with the next one showing at the fold,
 * which is the point: a board is a comparison between columns, and a board that
 * folded into a stack would be the list this tab already has. The operator who
 * wants the list has it one tap away in the same toolbar, which is a better fold
 * than a breakpoint guessing for them. Nothing here joins the `.crm-weave`
 * column layout - that grid is the overview's and the task page's - so the
 * order-class rule at `.crm-weave` in `index.css` does not apply to these cards.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Flag, Loader2, TriangleAlert } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { getMilestoneBoard, type MilestoneBoardColumn } from "../../../lib/milestoneBoard";
import { updateTask, type Task } from "../../../lib/projects";
import { EmptyState, useToast } from "../../ui";
import { ScrollFade } from "../_shared/ScrollFade";
import { taskErrorKey } from "../tasks/taskErrors";
import { ColumnEmpty } from "./RoadmapCard";
import { BoardCard, ColumnHead, UNFILED } from "./MilestoneBoardCard";
import { moveTaskBetweenColumns, type ColumnKey } from "./milestoneBoardMove";

export function MilestoneBoard({ projectId, onChange }: { projectId: string; onChange: () => void }) {
  const t = useT();
  const toast = useToast();
  const [columns, setColumns] = useState<MilestoneBoardColumn[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  /**
   * The request did not come back.
   *
   * A separate state rather than "no columns", and it is the one piece of
   * bookkeeping in this file that exists purely to avoid telling a lie. Without
   * it a failed fetch leaves `columns` null, which reads exactly like a project
   * that has no phases yet - so a network blip would draw as a confident "No
   * milestones." over a project with eight of them.
   */
  const [failed, setFailed] = useState(false);

  /**
   * `silent` is for the reload that follows a refused move. The board is already
   * showing the restored columns by then, and swapping them for a spinner would
   * hide the very thing the operator has to see - that the card went back.
   */
  const load = useCallback(
    async (silent?: boolean) => {
      if (!silent) setLoading(true);
      try {
        const board = await getMilestoneBoard(projectId);
        setColumns(board.columns);
        setTruncated(board.truncated);
        setFailed(false);
      } catch {
        // The columns are left as they were. On a first load that is nothing,
        // and the failure is drawn below; on the silent reload after a refused
        // move it is the restored board, which is the right thing to be looking
        // at when the network is also having a bad minute.
        setFailed(true);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Every column of this board as a destination, the unfiled one included.
   *
   * Built from the columns rather than from a milestone list loaded elsewhere,
   * so the phases offered are exactly the phases drawn. A menu that offered a
   * phase with no column would move a card somewhere the operator cannot see it.
   */
  const options = useMemo(
    () =>
      (columns ?? []).map((c) => ({
        value: c.milestoneId ?? UNFILED,
        label: c.milestone ? c.milestone.name : t("projects.noMilestone"),
      })),
    [columns, t],
  );

  async function move(task: Task, from: ColumnKey, to: ColumnKey) {
    const before = columns;
    if (!before) return;
    const next = moveTaskBetweenColumns(before, task.id, from, to);
    // Unchanged means there was nothing to move - the same column, or a card
    // this board is not holding. No write goes out for a move that did not
    // happen; a failure then would have nothing to put back.
    if (next === before) return;

    setColumns(next);
    try {
      await updateTask(task.id, { milestoneId: to });
      // The phase rollup on the list view and the counters above the tab are
      // both computed from the same tasks, so they are now out of date.
      onChange();
    } catch (err) {
      setColumns(before);
      toast(t(taskErrorKey(err)));
      // The restore puts the card back where it was; the reload says what the
      // server actually holds. Both are needed: one of the two refusals means
      // the phase has been DELETED, and a board that only snapped the card back
      // would keep offering the column that caused the failure.
      void load(true);
    }
  }

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  // Said before the empty state, because the two are indistinguishable from the
  // outside and only one of them is about the project.
  if (failed && !columns) {
    return (
      <div className="crm-card">
        <EmptyState icon={TriangleAlert} title={t("common.error")} />
      </div>
    );
  }

  // A project with no phases is not a board with one nameless column - that is
  // the list this tab already draws, wearing a column. It is the same empty
  // state the list view shows for the same project, so the two views agree with
  // each other, and it sits directly under the quick-add field that fixes it.
  //
  // The trade, said out loud: the unfiled cards are not drawn in this one case.
  // That is the case where "unfiled" carries no information - there is nothing
  // to be filed under yet - and those tasks are listed in full on the tasks tab
  // next door, which is where they have always been.
  const phases = (columns ?? []).filter((c) => c.milestoneId !== null);
  if (phases.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={Flag} title={t("projects.noMilestones")} />
      </div>
    );
  }

  const shown = (columns ?? []).reduce((n, c) => n + c.tasks.length, 0);
  const held = (columns ?? []).reduce((n, c) => n + c.total, 0);

  return (
    <>
      <ScrollFade className="pb-2" deps={[columns]}>
        <div className="flex min-w-max gap-3.5">
          {(columns ?? []).map((column) => (
            <div
              key={column.milestoneId ?? UNFILED}
              className="crm-card flex w-[272px] shrink-0 flex-col gap-2.5 p-3.5"
            >
              <ColumnHead
                column={column}
                name={column.milestone ? column.milestone.name : t("projects.noMilestone")}
              />

              {column.tasks.map((task) => (
                <BoardCard
                  key={task.id}
                  task={task}
                  column={column.milestoneId}
                  options={options}
                  onMove={(tk, from, to) => void move(tk, from, to)}
                />
              ))}

              {/* Deliberately not an `EmptyState`: a row of illustrated empties
                  would shout louder than the work. An empty column still has to
                  be drawn - it is the only thing a card can be moved onto. */}
              {column.tasks.length === 0 ? <ColumnEmpty label={t("tasks.emptyCol")} /> : null}
            </div>
          ))}
        </div>
      </ScrollFade>

      {/* Only on a board the server had to cut short. The columns then hold the
          first page of cards while every header counts the whole phase, and this
          line is what keeps the two from disagreeing in silence. The wording is
          the tasks tab's own footer, so "showing 100 of 137" means the same
          thing on both. */}
      {truncated ? (
        <div className="px-1.5 pb-1">
          <span className="crm-mut text-[12.25px]">{t("projects.showingTasks", { shown, n: held })}</span>
        </div>
      ) : null}
    </>
  );
}
